import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useSupabaseAuth } from '../hooks/useSupabase';

interface Task {
  id: string;
  title: string;
  color?: string;
  completed: boolean;
  created_at: string;
}

interface TaskContextType {
  tasks: Task[];
  loading: boolean;
  loadTasks: () => Promise<void>;
  addTask: (title: string, color?: string) => Promise<void>;
  toggleTask: (taskId: string, completed: boolean) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  editTask: (taskId: string, title?: string, color?: string) => Promise<void>;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useSupabaseAuth();

  const loadTasks = async () => {
    try {
      setLoading(true);
      
      // Only load tasks if user is authenticated
      if (!isAuthenticated) {
        setTasks([]);
        return;
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('calendar_tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTasks(data || []);
    } catch (err) {
      console.error('Error loading tasks:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addTask = async (title: string, color?: string) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('calendar_tasks')
        .insert([{ 
          title: title.trim(),
          user_id: user.id,
          color: color || '#4f46e5'
        }])
        .select()
        .single();

      if (error) throw error;
      setTasks(prev => [data, ...prev]);
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  };

  const toggleTask = async (taskId: string, completed: boolean) => {
    try {
      const { error } = await supabase
        .from('calendar_tasks')
        .update({ completed })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev =>
        prev.map(task =>
          task.id === taskId ? { ...task, completed } : task
        )
      );
    } catch (err) {
      console.error('Error toggling task:', err);
      throw err;
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('calendar_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  const editTask = async (taskId: string, title?: string, color?: string) => {
    try {
      const updates: { title?: string; color?: string } = {};
      if (title !== undefined) updates.title = title.trim();
      if (color !== undefined) updates.color = color;

      const { error } = await supabase
        .from('calendar_tasks')
        .update(updates)
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev =>
        prev.map(task =>
          task.id === taskId ? { ...task, ...updates } : task
        )
      );
    } catch (err) {
      console.error('Error editing task:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadTasks();
    } else {
      setTasks([]);
    }
  }, [isAuthenticated]);

  const value = {
    tasks,
    loading,
    loadTasks,
    addTask,
    toggleTask,
    deleteTask, 
    editTask
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};